import type { FC } from 'react'
import { useCallback, useState } from 'react'

import { InfoBox } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useIsThreeStepIT } from '@/hooks/access'
import { APP_ROUTES } from '@/util/app-routes'

import { AccountSettingsForm } from './AccountSettingsForm'
import type { PasswordConditions } from './helpers'
import { deriveColorConditions } from './helpers'

import './AccountSettings.styles.scss'

export const AccountSettings: FC = () => {
  const navigate = useNavigate()
  const isThreeStepIT = useIsThreeStepIT()

  const [, setPasswordConditions] = useState<PasswordConditions>({
    upperAndLower: false,
    number: false,
    specialChar: false,
    length8: false,
    length16: false,
  })

  const [colorConditions, setColorConditions] = useState({
    upperAndLower: 'text-yellow-700 bg-yellow-200',
    number: 'text-yellow-700 bg-yellow-200',
    specialChar: 'text-yellow-700 bg-yellow-200',
    length8: 'text-yellow-700 bg-yellow-200',
    length16: 'text-yellow-700 bg-yellow-200',
  })

  const handlePasswordConditionsChange = useCallback(
    (data: { conditions: PasswordConditions; newPassword: string }) => {
      setPasswordConditions(data.conditions)
      const newColorConditions = deriveColorConditions(data.conditions, data.newPassword)
      setColorConditions(newColorConditions)
    },
    []
  )

  return (
    <Container
      onSwipedLeft={() => navigate(APP_ROUTES.HOME)}
      className="admin-account-settings flex flex-col items-center"
    >
      <div className="mobile:w-2/3 w-full">
        <InfoBox marginBottom={4} marginTop={4} className="flex justify-center">
          <div
            className={twJoin(
              'text mb-3 flex flex-col gap-3 p-3 text-sm',
              !isThreeStepIT && 'text-gray'
            )}
          >
            <Translation
              id="Passwords are required to contain {length8}, {upperAndLower}, {number}, and {specialChar}."
              length8={<Translation id="8 characters" className={colorConditions.length8} />}
              upperAndLower={
                <Translation
                  id="upper and lower case letters"
                  className={colorConditions.upperAndLower}
                />
              }
              number={<Translation id="one number" className={colorConditions.number} />}
              specialChar={
                <Translation id="one special character" className={colorConditions.specialChar} />
              }
            />
            <div className="mb-3">
              <Translation
                id="Alternatively, the password has to be at least {length16}."
                length16={<Translation id="16 characters" className={colorConditions.length16} />}
              />
            </div>
            <Translation
              id="A long password will always be stronger than a shorter one. A good rule is therefore to
          think pass-phrase instead of pass-word. Try to think of a short sentence that is relevant
          to you specifically."
            />
          </div>
        </InfoBox>
      </div>
      <Container.Header>
        <Translation id="Account Settings" />
      </Container.Header>
      <AccountSettingsForm onPasswordConditionsChange={handlePasswordConditionsChange} />
    </Container>
  )
}
